import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type SchedulingSettings } from '../../lib/api.js';
import { useAuth } from '../../lib/auth-store.js';
import {
  SCHEDULING_FALLBACK,
  SCHEDULING_SETTINGS_KEY,
  hourLabel,
  useInvalidateSchedulingSettings,
} from '../../lib/settings.js';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';

const HOURS = Array.from({ length: 24 }, (_, h) => h);

/**
 * Admin → Settings → Scheduling. Company-wide defaults for the dispatch
 * board: how long a freshly dropped job is, and the day window the board
 * shows. Admins edit; everyone else sees the values read-only.
 */
export function SchedulingSettingsPanel() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin';
  const invalidate = useInvalidateSchedulingSettings();

  const query = useQuery({
    queryKey: SCHEDULING_SETTINGS_KEY,
    queryFn: ({ signal }) => api.settings.scheduling(signal),
    staleTime: 60_000,
  });
  const saved = query.data ?? SCHEDULING_FALLBACK;

  const [draft, setDraft] = useState<SchedulingSettings>(saved);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setDraft(saved), [saved]);

  const save = useMutation<SchedulingSettings, ApiError, SchedulingSettings>({
    mutationFn: (body) => api.settings.updateScheduling(body),
    onSuccess: async () => {
      setNotice('Saved. The board picks this up on its next refresh.');
      await invalidate();
      setTimeout(() => setNotice(null), 4000);
    },
  });

  const dirty =
    draft.defaultJobHours !== saved.defaultJobHours ||
    draft.dayStartHour !== saved.dayStartHour ||
    draft.dayEndHour !== saved.dayEndHour;
  const dayLength = draft.dayEndHour - draft.dayStartHour;
  const dayProblem =
    dayLength < 4 ? 'The board day must be at least 4 hours.' : dayLength > 16 ? 'The board day can be at most 16 hours.' : null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canEdit || !dirty || dayProblem) return;
    save.mutate(draft);
  };

  return (
    <section
      className="rounded-xl border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-labelledby="scheduling-settings-title"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="scheduling-settings-title" className="font-medium text-ink">
            Scheduling settings
          </h2>
          <p className="text-xs text-ink-muted mt-0.5">
            Company-wide defaults for the Operations board.
            {!canEdit && ' Only admins can change these.'}
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-4 grid gap-4 sm:grid-cols-3">
        <label className="space-y-1 text-sm block">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Default job length</span>
          <select
            value={draft.defaultJobHours}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, defaultJobHours: Number(e.target.value) })}
            className="h-11 w-full rounded-lg border bg-surface px-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand disabled:opacity-60"
            style={{ borderColor: 'var(--color-border-secondary)' }}
          >
            {Array.from({ length: 24 }, (_, i) => (i + 1) / 2).map((h) => (
              <option key={h} value={h}>
                {h === 0.5 ? '30 minutes' : `${Number.isInteger(h) ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}`}
              </option>
            ))}
          </select>
          <span className="block text-[11px] text-ink-subtle">
            How long a job takes on the board when it's first dropped onto a tech.
          </span>
        </label>

        <label className="space-y-1 text-sm block">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Day starts</span>
          <select
            value={draft.dayStartHour}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, dayStartHour: Number(e.target.value) })}
            className="h-11 w-full rounded-lg border bg-surface px-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand disabled:opacity-60"
            style={{ borderColor: 'var(--color-border-secondary)' }}
          >
            {HOURS.slice(0, 23).map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 text-sm block">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Day ends</span>
          <select
            value={draft.dayEndHour}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, dayEndHour: Number(e.target.value) })}
            className="h-11 w-full rounded-lg border bg-surface px-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand disabled:opacity-60"
            style={{ borderColor: 'var(--color-border-secondary)' }}
          >
            {HOURS.slice(1).concat(24).map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
          <span className="block text-[11px] text-ink-subtle">
            {dayProblem ?? `${dayLength}-hour board day. Dispatchers can still widen their own view.`}
          </span>
        </label>

        {canEdit && (
          <div className="sm:col-span-3 flex items-center gap-3">
            <Button type="submit" size="sm" disabled={!dirty || !!dayProblem || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save settings'}
            </Button>
            {dirty && !save.isPending && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(saved)}>
                Discard
              </Button>
            )}
            {notice && <span className="text-xs text-emerald-700">{notice}</span>}
          </div>
        )}
        {save.isError && (
          <div className="sm:col-span-3">
            <Feedback variant="error" size="sm">
              Couldn't save: {save.error.message}
            </Feedback>
          </div>
        )}
        {query.isError && (
          <div className="sm:col-span-3">
            <Feedback variant="warning" size="sm">
              Couldn't load the saved settings — showing the built-in defaults.
            </Feedback>
          </div>
        )}
      </form>
    </section>
  );
}
